import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSitemap, FaPlus, FaSpinner, FaTimes } from 'react-icons/fa';
import * as planCuentasService from '../services/planCuentasService';
import '../styles/pages/PlanCuentasPage.css';

const TIPO_LABELS = {
  ACTIVO: 'Activo',
  PASIVO: 'Pasivo',
  GANANCIA: 'Ganancia',
  PERDIDA: 'Pérdida',
};

const ESTADO_LABELS = {
  PENDIENTE: 'Pendiente',
  APROBADA: 'Aprobada',
  RECHAZADA: 'Rechazada',
};

const FORM_VACIO = { id_plan_sii: '', nombre_cuenta: '', tipo_cuenta: '', afecta_f22: null, id_concepto_f22: '', motivo: '' };

const raiz = (codigo) => codigo.split('.')[0];
const prefijoGrupo = (codigo) => codigo.split('.').slice(0, 2).join('.');
const normalizar = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// El tipo lo fija el grupo de la partida: 1.x activo y 2.x pasivo. En 3.x lo elige el usuario.
const tipoPorPartida = (codigo) => {
  if (!codigo) return null;
  if (codigo.startsWith('1.')) return 'ACTIVO';
  if (codigo.startsWith('2.')) return 'PASIVO';
  return null;
};

/**
 * Arma el árbol del plan del SII: bloques (ACTIVOS, PASIVOS, ESTADO DE RESULTADOS) →
 * grupos (1.01.00.00) → partidas (1.01.01.00) → nuestras cuentas (1.01.01.02 BANCO).
 * La Sección II del Anexo no trae un grupo raíz como 1.00.00.00; su bloque toma el
 * título de la sección.
 */
const construirArbol = (plan, cuentas) => {
  const cuentasPorPartida = {};
  cuentas.forEach((c) => { (cuentasPorPartida[c.id_plan_sii] = cuentasPorPartida[c.id_plan_sii] || []).push(c); });

  const grupos = plan.filter((p) => p.nivel === 'GRUPO');
  const partidas = plan.filter((p) => p.nivel === 'PARTIDA');
  const subgrupos = grupos.filter((g) => !g.codigo.endsWith('.00.00.00'));
  const raices = [...new Set(subgrupos.map((g) => raiz(g.codigo)))];

  return raices.map((r) => {
    const tope = grupos.find((g) => g.codigo === `${r}.00.00.00`);
    return {
      id: r,
      titulo: tope ? tope.descripcion : 'ESTADO DE RESULTADOS',
      grupos: subgrupos
        .filter((g) => raiz(g.codigo) === r)
        .map((g) => ({
          ...g,
          partidas: partidas
            .filter((p) => prefijoGrupo(p.codigo) === prefijoGrupo(g.codigo))
            .map((p) => ({ ...p, cuentas: cuentasPorPartida[p.id] || [] })),
        })),
    };
  });
};

const fmtFecha = (valor) => (valor
  ? new Date(valor).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Santiago',
  })
  : '');

/**
 * Plan de cuentas: el plan del SII (Anexo DJ 1847 y 1926) con nuestras cuentas bajo cada
 * partida, y las propuestas de cuentas nuevas. Una cuenta propuesta queda pendiente y no
 * se puede usar hasta que el administrador la aprueba. Las cuentas son las mismas para
 * todas las empresas.
 */
const PlanCuentasPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('plan');
  const [datos, setDatos] = useState({ plan: [], cuentas: [], anio_tributario: null });
  const [conceptos, setConceptos] = useState([]);
  const [propuestas, setPropuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [soloConCuentas, setSoloConCuentas] = useState(true);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [plan, listaConceptos, lista] = await Promise.all([
        planCuentasService.getPlanCuentas(),
        planCuentasService.getConceptosF22(),
        planCuentasService.getMisPropuestas(),
      ]);
      setDatos(plan);
      setConceptos(listaConceptos);
      setPropuestas(lista);
    } catch (err) {
      console.error('[PlanCuentasPage] Error al cargar:', err);
      setError(err.response?.data?.message || 'No se pudo cargar el plan de cuentas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const arbol = useMemo(() => construirArbol(datos.plan, datos.cuentas), [datos]);

  // Una partida se ve si coincide con la búsqueda ella o alguna de sus cuentas
  const arbolVisible = useMemo(() => {
    const q = normalizar(busqueda.trim());
    const coincide = (p) => !q
      || normalizar(`${p.codigo} ${p.descripcion}`).includes(q)
      || p.cuentas.some((c) => normalizar(`${c.codigo} ${c.nombre_cuenta}`).includes(q));

    return arbol
      .map((b) => ({
        ...b,
        grupos: b.grupos
          .map((g) => ({ ...g, partidas: g.partidas.filter((p) => (!soloConCuentas || p.cuentas.length > 0) && coincide(p)) }))
          .filter((g) => g.partidas.length > 0),
      }))
      .filter((b) => b.grupos.length > 0);
  }, [arbol, busqueda, soloConCuentas]);

  const partidaElegida = datos.plan.find((p) => String(p.id) === String(form.id_plan_sii));
  const tipoFijo = tipoPorPartida(partidaElegida?.codigo);

  const abrirModal = (idPartida = '') => {
    setForm({ ...FORM_VACIO, id_plan_sii: idPartida ? String(idPartida) : '' });
    setErrorForm('');
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    if (guardando) return;
    setModalAbierto(false);
  };

  const setCampo = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const enviarPropuesta = async (e) => {
    e.preventDefault();
    setErrorForm('');

    if (form.afecta_f22 === null) {
      setErrorForm('Indica si la cuenta afecta o no el F22');
      return;
    }

    setGuardando(true);
    try {
      await planCuentasService.crearPropuesta({
        id_plan_sii: Number(form.id_plan_sii),
        nombre_cuenta: form.nombre_cuenta,
        tipo_cuenta: tipoFijo || form.tipo_cuenta,
        afecta_f22: form.afecta_f22,
        id_concepto_f22: form.afecta_f22 && form.id_concepto_f22 ? Number(form.id_concepto_f22) : null,
        motivo: form.motivo,
      });
      setModalAbierto(false);
      setAviso(`Propuesta de ${form.nombre_cuenta.trim().toUpperCase()} enviada. Queda pendiente hasta que el administrador la apruebe.`);
      setPropuestas(await planCuentasService.getMisPropuestas());
      setTab('propuestas');
    } catch (err) {
      setErrorForm(err.response?.data?.message || 'No se pudo enviar la propuesta');
    } finally {
      setGuardando(false);
    }
  };

  const pendientes = propuestas.filter((p) => p.estado === 'PENDIENTE').length;

  return (
    <div className="pc-page">
      <header className="pc-header">
        <button className="pc-back-btn" onClick={() => navigate('/dashboard')}>
          <FaArrowLeft /> Volver
        </button>
        <div className="pc-header-info">
          <h1 className="pc-title"><FaSitemap /> Plan de cuentas</h1>
        </div>
        <button className="pc-btn pc-btn--primary" onClick={() => abrirModal()}>
          <FaPlus /> Proponer cuenta
        </button>
      </header>

      <div className="pc-tabs">
        <button className={`pc-tab${tab === 'plan' ? ' pc-tab--activa' : ''}`} onClick={() => setTab('plan')}>
          Plan de cuentas
        </button>
        <button className={`pc-tab${tab === 'propuestas' ? ' pc-tab--activa' : ''}`} onClick={() => setTab('propuestas')}>
          Mis propuestas
          {pendientes > 0 && <span className="pc-badge">{pendientes}</span>}
        </button>
      </div>

      {aviso && (
        <div className="pc-aviso">
          <span>{aviso}</span>
          <button className="pc-aviso-cerrar" onClick={() => setAviso('')} aria-label="Cerrar aviso"><FaTimes /></button>
        </div>
      )}

      {loading ? (
        <div className="pc-loading"><FaSpinner className="pc-spinner" /> Cargando...</div>
      ) : error ? (
        <p className="pc-error">{error}</p>
      ) : tab === 'plan' ? (
        <>
          <div className="pc-filtros">
            <input
              className="pc-input pc-busqueda"
              placeholder="Buscar código o nombre..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <label className="pc-check">
              <input type="checkbox" checked={soloConCuentas} onChange={(e) => setSoloConCuentas(e.target.checked)} />
              Solo partidas con cuentas
            </label>
          </div>

          {arbolVisible.length === 0 ? (
            <p className="pc-vacio">No hay partidas que coincidan con la búsqueda</p>
          ) : (
            <div className="pc-arbol">
              {arbolVisible.map((bloque) => (
                <section key={bloque.id} className="pc-bloque">
                  <h2 className="pc-bloque-titulo">{bloque.titulo}</h2>
                  {bloque.grupos.map((grupo) => (
                    <div key={grupo.id} className="pc-grupo">
                      <h3 className="pc-grupo-titulo">
                        <span className="pc-codigo">{grupo.codigo}</span> {grupo.descripcion}
                      </h3>
                      {grupo.partidas.map((partida) => (
                        <div key={partida.id} className="pc-partida">
                          <div className="pc-partida-fila">
                            <span className="pc-codigo">{partida.codigo}</span>
                            <span className="pc-partida-nombre">{partida.descripcion}</span>
                            <button className="pc-btn pc-btn--chico pc-btn--primary" onClick={() => abrirModal(partida.id)}>
                              Proponer aquí
                            </button>
                          </div>
                          {partida.cuentas.map((cuenta) => (
                            <div key={cuenta.id} className="pc-cuenta">
                              <span className="pc-codigo">{cuenta.codigo}</span>
                              <span className="pc-cuenta-nombre">{cuenta.nombre_cuenta}</span>
                              <span className={`pc-tipo pc-tipo--${cuenta.tipo_cuenta.toLowerCase()}`}>
                                {TIPO_LABELS[cuenta.tipo_cuenta] || cuenta.tipo_cuenta}
                              </span>
                              <span className="pc-f22" title={cuenta.concepto_f22 || ''}>
                                {cuenta.codigos_f22 ? `F22: ${cuenta.codigos_f22}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          )}
        </>
      ) : propuestas.length === 0 ? (
        <p className="pc-vacio">Aún no has propuesto cuentas</p>
      ) : (
        <div className="pc-tabla-wrapper">
          <table className="pc-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cuenta</th>
                <th>Partida</th>
                <th>Tipo</th>
                <th>Afecta F22</th>
                <th>Estado</th>
                <th>Código</th>
                <th>Motivo de rechazo</th>
              </tr>
            </thead>
            <tbody>
              {propuestas.map((p) => (
                <tr key={p.id}>
                  <td className="pc-fecha">{fmtFecha(p.propuesto_en)}</td>
                  <td className="pc-cuenta-nombre" title={p.motivo}>{p.nombre_cuenta}</td>
                  <td><span className="pc-codigo">{p.codigo_sii}</span> {p.partida}</td>
                  <td>{TIPO_LABELS[p.tipo_cuenta] || p.tipo_cuenta}</td>
                  <td>{p.afecta_f22 ? (p.concepto_f22 ? `Sí · ${p.concepto_f22}` : 'Sí') : 'No'}</td>
                  <td><span className={`pc-estado pc-estado--${p.estado.toLowerCase()}`}>{ESTADO_LABELS[p.estado] || p.estado}</span></td>
                  <td className="pc-codigo">{p.codigo_asignado || ''}</td>
                  <td>{p.motivo_rechazo || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAbierto && (
        <div className="pc-modal-fondo" onClick={cerrarModal}>
          <form
            className="pc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pc-modal-titulo"
            onClick={(e) => e.stopPropagation()}
            onSubmit={enviarPropuesta}
          >
            <div className="pc-modal-header">
              <h2 id="pc-modal-titulo">Proponer cuenta</h2>
              <button type="button" className="pc-aviso-cerrar" onClick={cerrarModal} aria-label="Cerrar"><FaTimes /></button>
            </div>

            <label className="pc-campo">
              <span>Partida del SII</span>
              <select
                className="pc-input"
                value={form.id_plan_sii}
                onChange={(e) => setForm((prev) => ({ ...prev, id_plan_sii: e.target.value, tipo_cuenta: '' }))}
                required
              >
                <option value="">Elige la partida</option>
                {arbol.map((bloque) => bloque.grupos.map((grupo) => (
                  <optgroup key={grupo.id} label={`${grupo.codigo} ${grupo.descripcion}`}>
                    {grupo.partidas.map((p) => (
                      <option key={p.id} value={p.id}>{p.codigo} {p.descripcion}</option>
                    ))}
                  </optgroup>
                )))}
              </select>
            </label>

            <label className="pc-campo">
              <span>Nombre de la cuenta</span>
              <input
                className="pc-input"
                value={form.nombre_cuenta}
                onChange={(e) => setCampo('nombre_cuenta', e.target.value.toUpperCase())}
                maxLength={100}
                placeholder="Ej.: SEGUROS"
                required
              />
            </label>

            {partidaElegida && (
              <div className="pc-campo">
                <span>Tipo</span>
                {tipoFijo ? (
                  <p className="pc-tipo-fijo">{TIPO_LABELS[tipoFijo]} (lo fija la partida)</p>
                ) : (
                  <select
                    className="pc-input"
                    value={form.tipo_cuenta}
                    onChange={(e) => setCampo('tipo_cuenta', e.target.value)}
                    required
                  >
                    <option value="">Elige ganancia o pérdida</option>
                    <option value="GANANCIA">Ganancia</option>
                    <option value="PERDIDA">Pérdida</option>
                  </select>
                )}
              </div>
            )}

            <fieldset className="pc-campo pc-radios">
              <legend>¿Afecta el F22?</legend>
              <label>
                <input type="radio" name="afecta_f22" checked={form.afecta_f22 === true} onChange={() => setCampo('afecta_f22', true)} /> Sí
              </label>
              <label>
                <input
                  type="radio"
                  name="afecta_f22"
                  checked={form.afecta_f22 === false}
                  onChange={() => setForm((prev) => ({ ...prev, afecta_f22: false, id_concepto_f22: '' }))}
                /> No
              </label>
            </fieldset>

            {form.afecta_f22 === true && (
              <label className="pc-campo">
                <span>Línea del F22 sugerida (opcional)</span>
                <select
                  className="pc-input"
                  value={form.id_concepto_f22}
                  onChange={(e) => setCampo('id_concepto_f22', e.target.value)}
                >
                  <option value="">Sin sugerencia</option>
                  {['INGRESO', 'GASTO'].map((naturaleza) => (
                    <optgroup key={naturaleza} label={naturaleza === 'INGRESO' ? 'Ingresos' : 'Gastos'}>
                      {conceptos.filter((c) => c.naturaleza === naturaleza).map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre} ({c.codigos})</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}

            <label className="pc-campo">
              <span>Motivo</span>
              <textarea
                className="pc-input"
                rows={3}
                value={form.motivo}
                onChange={(e) => setCampo('motivo', e.target.value)}
                placeholder="Para qué se necesita esta cuenta"
                required
              />
            </label>

            <p className="pc-nota">
              El código se asigna cuando el administrador aprueba; mientras tanto la cuenta no se puede usar.
            </p>

            {errorForm && <p className="pc-error">{errorForm}</p>}

            <div className="pc-modal-acciones">
              <button type="button" className="pc-btn" onClick={cerrarModal} disabled={guardando}>Cancelar</button>
              <button type="submit" className="pc-btn pc-btn--primary" disabled={guardando}>
                {guardando ? <><FaSpinner className="pc-spinner" /> Enviando...</> : 'Enviar propuesta'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PlanCuentasPage;
